import { Injectable, computed, signal } from '@angular/core';
import { GozemApiError } from '@shared/utils/http-error.util';

/**
 * PinAttemptStore
 *
 * Encapsulates PIN attempt tracking, lockout state, and the human-readable
 * error message shown below the keypad. Extracted from `WithdrawalStore` so the
 * fiddliest part of that flow — deriving the "N attempts remaining" / "locked"
 * message from a backend error — is isolated and unit-testable.
 *
 * Dialog-scoped: provided alongside the dialog store so a fresh instance is
 * created per dialog session. (`SendStore` / recover-pin can adopt it later.)
 */
@Injectable()
export class PinAttemptStore {
  private readonly _pinAttempts = signal<number>(0);
  private readonly _pinError = signal<string | null>(null);
  private readonly _pinLocked = signal<boolean>(false);

  readonly pinAttempts = this._pinAttempts.asReadonly();
  readonly pinError = this._pinError.asReadonly();
  readonly pinLocked = this._pinLocked.asReadonly();

  /**
   * Human-readable PIN error shown below the dot indicators.
   *
   * Examples:
   *  - "Code de sécurité incorrect — 2 tentatives restantes"
   *  - "Compte bloqué — trop de tentatives incorrectes"
   */
  readonly pinErrorMessage = computed((): string | null => {
    if (this._pinLocked()) {
      // Prefer the server's lockout message verbatim; fall back only if absent.
      return this._pinError() ?? 'Compte bloqué — trop de tentatives incorrectes.';
    }
    return this._pinError();
  });

  /**
   * Record a failed PIN verification: increments the attempt counter, then
   * derives the appropriate error / lockout state from the backend error.
   *
   * @param err - The error thrown by the verify_pin call
   */
  recordFailure(err: unknown): void {
    this._pinAttempts.set(this._pinAttempts() + 1);
    this.applyError(err);
  }

  /** Clear only the inline error message (e.g. when starting a new attempt). */
  clearError(): void {
    this._pinError.set(null);
  }

  /** Clear attempts + error + lock. Call on success, back-to-form, or reset. */
  reset(): void {
    this._pinAttempts.set(0);
    this._pinError.set(null);
    this._pinLocked.set(false);
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  /**
   * Sets the appropriate PIN error message after a failed verify_pin (or PIN-
   * carrying init) call.
   *
   * The backend is the single source of truth. A `GozemApiError` carries both
   * `remainingAttempts` and the ready-to-display server `message`
   * (`displayMessage`). We show that message verbatim and lock when
   * `remainingAttempts === 0`. Local attempt counting is only a last-resort
   * fallback for the (unexpected) case where the structured error is missing.
   */
  private applyError(err: unknown): void {
    const apiError = err instanceof GozemApiError ? err : null;
    const remaining = apiError?.remainingAttempts ?? null;
    const serverMessage = apiError?.displayMessage ?? null;

    // Lockout — backend authority (remaining_attempts === 0).
    if (remaining === 0) {
      this._pinLocked.set(true);
      this._pinError.set(serverMessage);
      return;
    }

    // Wrong PIN, attempts remaining — show the server message verbatim.
    if (serverMessage) {
      this._pinError.set(serverMessage);
      return;
    }

    // Last-resort fallback: no structured error reached us. Reconstruct from
    // whatever count we can infer so the user still sees something meaningful.
    const count = remaining ?? Math.max(0, 3 - this._pinAttempts());
    if (count === 0) {
      this._pinLocked.set(true);
      this._pinError.set(null);
    } else {
      this._pinError.set(
        `Code de sécurité incorrect — ${count} tentative${count > 1 ? 's' : ''} restante${count > 1 ? 's' : ''}`,
      );
    }
  }
}
